import React, { useMemo, useState } from 'react';
import { roomTaxesFromDiscounted } from '../../utils/roomPricing';
import { ROOM_DAY_KEYS, ROOM_DAY_LABELS } from '../../utils/roomDayPricing';
import {
  defaultGuestTiersFromBase,
  resolveGuestOccupancySlice
} from '../../utils/roomGuestPricing';

function TierTable({ tiers, cap, onSetTier }) {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm border border-gray-200 rounded-lg overflow-hidden">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Adults</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">MRP (₹/night)</th>
            <th className="px-3 py-2 text-left font-medium text-gray-700">Sell (₹/night)</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: cap }, (_, i) => i + 1).map((a) => {
            const row = tiers.find((t) => t.adults === a) || { adults: a, mrp: 0, discountedPrice: 0 };
            return (
              <tr key={a} className="border-t border-gray-100">
                <td className="px-3 py-2 text-gray-800">{a}</td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    value={row.mrp ?? ''}
                    onChange={(e) => onSetTier(a, 'mrp', e.target.value)}
                    className="w-full max-w-[140px] px-2 py-1 border border-gray-300 rounded"
                  />
                </td>
                <td className="px-3 py-2">
                  <input
                    type="number"
                    min="0"
                    value={row.discountedPrice ?? ''}
                    onChange={(e) => onSetTier(a, 'discountedPrice', e.target.value)}
                    className="w-full max-w-[140px] px-2 py-1 border border-gray-300 rounded"
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AddonFields({ threshold, cap, addon, onThreshold, onAddon }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Threshold adults (included in table)
        </label>
        <input
          type="number"
          min={1}
          max={cap}
          value={threshold}
          onChange={(e) => onThreshold(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Extra adult add-on MRP (₹/night each)
        </label>
        <input
          type="number"
          min="0"
          value={addon.mrp ?? ''}
          onChange={(e) => onAddon('mrp', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Extra adult add-on sell (₹/night each)
        </label>
        <input
          type="number"
          min="0"
          value={addon.discountedPrice ?? ''}
          onChange={(e) => onAddon('discountedPrice', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
        />
      </div>
    </div>
  );
}

/**
 * @param {{ price: object, setPrice: (next: object) => void, capacity: number }} props
 */
export default function RoomGuestPricingFields({ price, setPrice, capacity = 2 }) {
  const cap = Math.max(1, Math.min(20, Math.floor(Number(capacity) || 2)));
  const gp = price?.guestPricing || {};
  const enabled = Boolean(gp.enabled);
  const useDayBands = Boolean(price?.useDayOfWeekPricing);
  const dayPricing = price?.dayPricing && typeof price.dayPricing === 'object' ? price.dayPricing : {};
  const [activeDay, setActiveDay] = useState('sun');

  const defaultTiers = Array.isArray(gp.tiers) ? gp.tiers : [];
  const defaultThreshold = gp.thresholdAdults ?? cap;
  const defaultAddon = gp.extraAdultAddon || { mrp: 0, discountedPrice: 0 };

  const daySlice = dayPricing[activeDay] || {};
  const dayTiers = Array.isArray(daySlice.guestTiers) ? daySlice.guestTiers : [];
  const dayUsesCustom = dayTiers.length > 0;
  const dayThreshold = daySlice.thresholdAdults ?? defaultThreshold;
  const dayAddon = daySlice.extraAdultAddon || defaultAddon;

  const previewConfig = useDayBands && dayUsesCustom
    ? { tiers: dayTiers, thresholdAdults: dayThreshold, extraAdultAddon: dayAddon }
    : { tiers: defaultTiers, thresholdAdults: defaultThreshold, extraAdultAddon: defaultAddon };

  const previewAdults = Math.min(cap, Math.max(1, previewConfig.thresholdAdults + 1));
  const preview = useMemo(
    () =>
      enabled && previewConfig.tiers.length
        ? resolveGuestOccupancySlice(previewConfig, previewAdults)
        : null,
    [enabled, previewConfig, previewAdults]
  );

  const setDefaultTier = (adults, field, raw) => {
    const num = parseFloat(raw) || 0;
    const next = [...defaultTiers];
    let row = next.find((t) => t.adults === adults);
    if (!row) {
      row = { adults, mrp: 0, discountedPrice: 0 };
      next.push(row);
    }
    row[field] = num;
    if (field === 'discountedPrice') row.taxes = roomTaxesFromDiscounted(num);
    next.sort((a, b) => a.adults - b.adults);
    setPrice({ ...price, guestPricing: { ...gp, enabled: true, tiers: next } });
  };

  const setDayTier = (adults, field, raw) => {
    const num = parseFloat(raw) || 0;
    const next = [...dayTiers];
    let row = next.find((t) => t.adults === adults);
    if (!row) {
      row = { adults, mrp: 0, discountedPrice: 0 };
      next.push(row);
    }
    row[field] = num;
    if (field === 'discountedPrice') row.taxes = roomTaxesFromDiscounted(num);
    next.sort((a, b) => a.adults - b.adults);
    setPrice({
      ...price,
      dayPricing: {
        ...dayPricing,
        [activeDay]: {
          ...daySlice,
          guestTiers: next
        }
      }
    });
  };

  const ensureDefaultTiers = () => {
    if (defaultTiers.length >= cap) return;
    const built = defaultGuestTiersFromBase({
      mrp: price.mrp,
      discountedPrice: price.discountedPrice,
      capacity: cap
    });
    setPrice({
      ...price,
      guestPricing: {
        enabled: true,
        tiers: built,
        thresholdAdults: cap,
        extraAdultAddon: defaultAddon
      }
    });
  };

  const enableDayCustom = () => {
    const base =
      dayTiers.length > 0
        ? dayTiers
        : defaultTiers.length
          ? defaultTiers.map((t) => ({ ...t }))
          : defaultGuestTiersFromBase({
              mrp: daySlice.mrp ?? price.mrp,
              discountedPrice: daySlice.discountedPrice ?? price.discountedPrice,
              capacity: cap
            });
    setPrice({
      ...price,
      dayPricing: {
        ...dayPricing,
        [activeDay]: {
          ...daySlice,
          guestTiers: base,
          thresholdAdults: daySlice.thresholdAdults ?? defaultThreshold,
          extraAdultAddon: daySlice.extraAdultAddon || { ...defaultAddon }
        }
      }
    });
  };

  const copyDefaultToAllDays = () => {
    if (!defaultTiers.length) return;
    const next = { ...dayPricing };
    for (const key of ROOM_DAY_KEYS) {
      next[key] = {
        ...(next[key] || {}),
        guestTiers: defaultTiers.map((t) => ({ ...t })),
        thresholdAdults: defaultThreshold,
        extraAdultAddon: { ...defaultAddon }
      };
    }
    setPrice({ ...price, dayPricing: next });
  };

  const copyDefaultToActiveDay = () => {
    setPrice({
      ...price,
      dayPricing: {
        ...dayPricing,
        [activeDay]: {
          ...daySlice,
          guestTiers: defaultTiers.map((t) => ({ ...t })),
          thresholdAdults: defaultThreshold,
          extraAdultAddon: { ...defaultAddon }
        }
      }
    });
  };

  const clearDayCustom = () => {
    const next = { ...dayPricing };
    const slice = { ...(next[activeDay] || {}) };
    delete slice.guestTiers;
    delete slice.thresholdAdults;
    delete slice.extraAdultAddon;
    next[activeDay] = slice;
    setPrice({ ...price, dayPricing: next });
  };

  return (
    <section className="mt-6 border-t border-gray-200 pt-6">
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            if (e.target.checked) ensureDefaultTiers();
            else {
              setPrice({
                ...price,
                guestPricing: {
                  enabled: false,
                  tiers: [],
                  thresholdAdults: cap,
                  extraAdultAddon: defaultAddon
                }
              });
            }
          }}
          className="w-4 h-4 rounded border-gray-300 text-blue-600"
        />
        <span className="text-sm font-medium text-gray-800">
          Price by number of adults (per night)
        </span>
      </label>
      <p className="text-xs text-gray-500 mt-2 ml-7">
        Each night uses that date&apos;s weekday. With &quot;different price by day of week&quot;
        enabled, you can set a separate adult tier table per day; otherwise one default table
        applies every night.
      </p>

      {enabled ? (
        <div className="mt-4 space-y-6">
          <div>
            <p className="text-sm font-medium text-gray-800 mb-2">Default adult tiers (all nights)</p>
            <TierTable tiers={defaultTiers} cap={cap} onSetTier={setDefaultTier} />
            <div className="mt-4">
              <AddonFields
                threshold={defaultThreshold}
                cap={cap}
                addon={defaultAddon}
                onThreshold={(raw) =>
                  setPrice({
                    ...price,
                    guestPricing: {
                      ...gp,
                      enabled: true,
                      tiers: defaultTiers,
                      thresholdAdults: Math.min(cap, Math.max(1, parseInt(raw, 10) || 1))
                    }
                  })
                }
                onAddon={(field, raw) => {
                  const num = parseFloat(raw) || 0;
                  setPrice({
                    ...price,
                    guestPricing: {
                      ...gp,
                      enabled: true,
                      tiers: defaultTiers,
                      thresholdAdults: defaultThreshold,
                      extraAdultAddon: {
                        ...defaultAddon,
                        [field]: num,
                        ...(field === 'discountedPrice'
                          ? { taxes: roomTaxesFromDiscounted(num) }
                          : {})
                      }
                    }
                  });
                }}
              />
            </div>
          </div>

          {useDayBands ? (
            <div className="border border-gray-200 rounded-lg p-4 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-medium text-gray-800">Per-weekday adult tiers (optional)</p>
                <button
                  type="button"
                  onClick={copyDefaultToAllDays}
                  className="text-xs px-3 py-1.5 rounded-full border border-gray-300 hover:bg-gray-50"
                >
                  Copy default to all days
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {ROOM_DAY_KEYS.map((key, idx) => {
                  const hasCustom = (dayPricing[key]?.guestTiers || []).length > 0;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setActiveDay(key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium border ${
                        activeDay === key
                          ? 'bg-gray-900 text-white border-gray-900'
                          : 'bg-white text-gray-700 border-gray-300'
                      }`}
                    >
                      {ROOM_DAY_LABELS[idx]}
                      {hasCustom ? ' •' : ''}
                    </button>
                  );
                })}
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={dayUsesCustom}
                  onChange={(e) => (e.target.checked ? enableDayCustom() : clearDayCustom())}
                  className="rounded border-gray-300"
                />
                Custom adult tiers for {ROOM_DAY_LABELS[ROOM_DAY_KEYS.indexOf(activeDay)]}
              </label>
              {dayUsesCustom ? (
                <>
                  <TierTable tiers={dayTiers} cap={cap} onSetTier={setDayTier} />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={copyDefaultToActiveDay}
                      className="text-xs px-3 py-1.5 rounded-full border border-gray-300 hover:bg-gray-50"
                    >
                      Reset this day from default
                    </button>
                  </div>
                  <AddonFields
                    threshold={dayThreshold}
                    cap={cap}
                    addon={dayAddon}
                    onThreshold={(raw) =>
                      setPrice({
                        ...price,
                        dayPricing: {
                          ...dayPricing,
                          [activeDay]: {
                            ...daySlice,
                            guestTiers: dayTiers,
                            thresholdAdults: Math.min(cap, Math.max(1, parseInt(raw, 10) || 1))
                          }
                        }
                      })
                    }
                    onAddon={(field, raw) => {
                      const num = parseFloat(raw) || 0;
                      setPrice({
                        ...price,
                        dayPricing: {
                          ...dayPricing,
                          [activeDay]: {
                            ...daySlice,
                            guestTiers: dayTiers,
                            thresholdAdults: dayThreshold,
                            extraAdultAddon: {
                              ...dayAddon,
                              [field]: num,
                              ...(field === 'discountedPrice'
                                ? { taxes: roomTaxesFromDiscounted(num) }
                                : {})
                            }
                          }
                        }
                      });
                    }}
                  />
                </>
              ) : (
                <p className="text-xs text-gray-500">
                  This weekday uses the default adult tier table above.
                </p>
              )}
            </div>
          ) : null}

          {preview ? (
            <p className="text-xs text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
              Example ({useDayBands && dayUsesCustom ? ROOM_DAY_LABELS[ROOM_DAY_KEYS.indexOf(activeDay)] : 'default'}):{' '}
              {previewAdults} adults → ₹{preview.discountedPrice.toLocaleString('en-IN')}/night (includes ₹
              {preview.taxes.toLocaleString('en-IN')} tax)
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
